import React, { useState, useEffect, useMemo } from 'react';
import { 
  Download, Filter, FileSpreadsheet, Users, Building2, Sun, 
  Calendar as CalendarIcon, Wrench, Sparkles, Battery, Zap, MapPin, ChevronDown,
  ChevronUp, RotateCcw, SlidersHorizontal, X, FileDown, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { 
  customerAPI, propertyAPI, installationAPI, appointmentAPI, 
  maintenanceAPI, cleaningAPI 
} from '../lib/api';
import { toast } from 'sonner';

// Filter Section Component
const FilterSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 rounded-lg transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

// CSV Export utility
const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    toast.error('Geen data om te exporteren');
    return;
  }

  // Create header row
  const headers = columns.map(col => col.label).join(';');
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = col.accessor(item);
      // Handle null/undefined
      if (value === null || value === undefined) value = '';
      // Escape quotes and wrap in quotes if contains separator or quotes
      value = String(value);
      if (value.includes(';') || value.includes('"') || value.includes('\n')) {
        value = '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    }).join(';');
  });

  const csv = [headers, ...rows].join('\n');
  
  // Add BOM for Excel compatibility with special characters
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  
  toast.success(`${data.length} rijen geëxporteerd naar ${filename}.csv`);
};

const Export = () => {
  const [activeTab, setActiveTab] = useState('customers');
  const [loading, setLoading] = useState(true);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  
  // All data
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [cleanings, setCleanings] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    // Installation filters
    hasSolarPanels: null,
    hasBattery: null,
    hasEVCharger: null,
    minPanels: 0,
    maxPanels: 100,
    // Location filters
    cities: [],
    // Customer filters
    languages: [],
    hasCompany: null,
    // Installation type filter
    installationTypes: [],
    // Date filters
    dateFrom: '',
    dateTo: '',
    // Appointment filters
    appointmentTypes: [],
    appointmentStatus: [],
  });

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const uniqueCities = [...new Set(properties.map(p => p.city).filter(Boolean))].sort();
    const uniqueLanguages = [...new Set(customers.map(c => c.language).filter(Boolean))];
    
    // Calculate panel counts per customer
    const customerPanelCounts = {};
    installations.forEach(inst => {
      if (inst.installation_type === 'solar_panels' && inst.number_of_panels) {
        const prop = properties.find(p => p.id === inst.property_id);
        if (prop?.customer_id) {
          customerPanelCounts[prop.customer_id] = (customerPanelCounts[prop.customer_id] || 0) + inst.number_of_panels;
        }
      }
    });
    
    return {
      cities: uniqueCities,
      languages: uniqueLanguages,
      customerPanelCounts
    };
  }, [customers, properties, installations]);

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [custsRes, propsRes, instsRes, apptsRes, maintRes, cleanRes] = await Promise.all([
        customerAPI.getAll(),
        propertyAPI.getAll(),
        installationAPI.getAll(),
        appointmentAPI.getAll(),
        maintenanceAPI.getAll(),
        cleaningAPI.getAll()
      ]);
      setCustomers(custsRes.data);
      setProperties(propsRes.data);
      setInstallations(instsRes.data);
      setAppointments(apptsRes.data);
      setMaintenance(maintRes.data);
      setCleanings(cleanRes.data);
    } catch (error) {
      toast.error('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Helper to get customer installations
  const getCustomerInstallations = (customerId) => {
    const customerProperties = properties.filter(p => p.customer_id === customerId);
    const propertyIds = customerProperties.map(p => p.id);
    return installations.filter(i => propertyIds.includes(i.property_id));
  };

  // Filtered data based on active tab and filters
  const filteredData = useMemo(() => {
    let result = [];

    switch (activeTab) {
      case 'customers':
        result = [...customers];
        
        // Installation filters
        if (filters.hasSolarPanels !== null) {
          result = result.filter(c => {
            const custInst = getCustomerInstallations(c.id);
            const has = custInst.some(i => i.installation_type === 'solar_panels');
            return filters.hasSolarPanels ? has : !has;
          });
        }
        if (filters.hasBattery !== null) {
          result = result.filter(c => {
            const custInst = getCustomerInstallations(c.id);
            const has = custInst.some(i => i.installation_type === 'battery');
            return filters.hasBattery ? has : !has;
          });
        }
        if (filters.hasEVCharger !== null) {
          result = result.filter(c => {
            const custInst = getCustomerInstallations(c.id);
            const has = custInst.some(i => i.installation_type === 'ev_charger');
            return filters.hasEVCharger ? has : !has;
          });
        }
        if (filters.minPanels > 0 || filters.maxPanels < 100) {
          result = result.filter(c => {
            const count = filterOptions.customerPanelCounts[c.id] || 0;
            return count >= filters.minPanels && count <= filters.maxPanels;
          });
        }
        if (filters.languages.length > 0) {
          result = result.filter(c => filters.languages.includes(c.language));
        }
        if (filters.cities.length > 0) {
          result = result.filter(c => {
            const customerProperties = properties.filter(p => p.customer_id === c.id);
            return customerProperties.some(p => filters.cities.includes(p.city));
          });
        }
        if (filters.hasCompany !== null) {
          result = result.filter(c => {
            const has = c.company_name && c.company_name.trim() !== '';
            return filters.hasCompany ? has : !has;
          });
        }
        break;

      case 'properties':
        result = [...properties];
        if (filters.cities.length > 0) {
          result = result.filter(p => filters.cities.includes(p.city));
        }
        break;

      case 'installations':
        result = [...installations];
        if (filters.installationTypes.length > 0) {
          result = result.filter(i => filters.installationTypes.includes(i.installation_type));
        }
        if (filters.cities.length > 0) {
          result = result.filter(i => {
            const prop = properties.find(p => p.id === i.property_id);
            return prop && filters.cities.includes(prop.city);
          });
        }
        break;

      case 'appointments':
        result = [...appointments];
        if (filters.appointmentTypes.length > 0) {
          result = result.filter(a => filters.appointmentTypes.includes(a.appointment_type));
        }
        if (filters.appointmentStatus.length > 0) {
          result = result.filter(a => filters.appointmentStatus.includes(a.status));
        }
        if (filters.dateFrom) {
          result = result.filter(a => a.date >= filters.dateFrom);
        }
        if (filters.dateTo) {
          result = result.filter(a => a.date <= filters.dateTo);
        }
        break;

      case 'maintenance':
        result = [...maintenance];
        if (filters.dateFrom) {
          result = result.filter(m => m.maintenance_date >= filters.dateFrom);
        }
        if (filters.dateTo) {
          result = result.filter(m => m.maintenance_date <= filters.dateTo);
        }
        break;

      case 'cleanings':
        result = [...cleanings];
        if (filters.dateFrom) {
          result = result.filter(c => c.cleaning_date >= filters.dateFrom);
        }
        if (filters.dateTo) {
          result = result.filter(c => c.cleaning_date <= filters.dateTo);
        }
        break;

      case 'all':
        // Combined export - returns object with all filtered data
        return {
          customers: customers.length,
          properties: properties.length,
          installations: installations.length,
          appointments: appointments.length,
          maintenance: maintenance.length,
          cleanings: cleanings.length
        };

      default:
        result = [];
    }

    return result;
  }, [activeTab, customers, properties, installations, appointments, maintenance, cleanings, filters, filterOptions]);

  // Column definitions for each entity
  const columnDefs = {
    customers: [
      { label: 'ID', accessor: (c) => c.id },
      { label: 'Voornaam', accessor: (c) => c.first_name },
      { label: 'Achternaam', accessor: (c) => c.last_name },
      { label: 'Bedrijf', accessor: (c) => c.company_name },
      { label: 'Email', accessor: (c) => c.email },
      { label: 'Telefoon', accessor: (c) => c.phone },
      { label: 'Taal', accessor: (c) => c.language },
      { label: 'BTW Nummer', accessor: (c) => c.vat_number },
      { label: 'Verkoper', accessor: (c) => c.sales_person },
      { label: 'Notities', accessor: (c) => c.notes },
      { label: 'Aantal Panelen', accessor: (c) => filterOptions.customerPanelCounts[c.id] || 0 },
      { label: 'Heeft Batterij', accessor: (c) => getCustomerInstallations(c.id).some(i => i.installation_type === 'battery') ? 'Ja' : 'Nee' },
      { label: 'Heeft Laadpaal', accessor: (c) => getCustomerInstallations(c.id).some(i => i.installation_type === 'ev_charger') ? 'Ja' : 'Nee' },
    ],
    properties: [
      { label: 'ID', accessor: (p) => p.id },
      { label: 'Klant ID', accessor: (p) => p.customer_id },
      { label: 'Klant', accessor: (p) => {
        const cust = customers.find(c => c.id === p.customer_id);
        return cust ? `${cust.first_name} ${cust.last_name}` : '';
      }},
      { label: 'Straat', accessor: (p) => p.street },
      { label: 'Huisnummer', accessor: (p) => p.number },
      { label: 'Postcode', accessor: (p) => p.postal_code },
      { label: 'Gemeente', accessor: (p) => p.city },
      { label: 'Land', accessor: (p) => p.country },
      { label: 'Type', accessor: (p) => p.property_type },
      { label: 'EAN Elektriciteit', accessor: (p) => p.ean_electricity },
      { label: 'EAN Gas', accessor: (p) => p.ean_gas },
    ],
    installations: [
      { label: 'ID', accessor: (i) => i.id },
      { label: 'Property ID', accessor: (i) => i.property_id },
      { label: 'Adres', accessor: (i) => {
        const prop = properties.find(p => p.id === i.property_id);
        return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
      }},
      { label: 'Type', accessor: (i) => {
        const types = { solar_panels: 'Zonnepanelen', battery: 'Batterij', ev_charger: 'Laadpaal', inverter: 'Omvormer' };
        return types[i.installation_type] || i.installation_type;
      }},
      { label: 'Merk', accessor: (i) => i.brand },
      { label: 'Model', accessor: (i) => i.model },
      { label: 'Aantal Panelen', accessor: (i) => i.number_of_panels },
      { label: 'Wattage per Paneel', accessor: (i) => i.panel_wattage },
      { label: 'Totaal kWp', accessor: (i) => i.number_of_panels && i.panel_wattage ? ((i.number_of_panels * i.panel_wattage) / 1000).toFixed(2) : '' },
      { label: 'Batterij Capaciteit', accessor: (i) => i.battery_capacity },
      { label: 'Laadpaal Vermogen', accessor: (i) => i.charger_power },
      { label: 'Omvormer Vermogen', accessor: (i) => i.inverter_power },
      { label: 'Installatie Datum', accessor: (i) => i.installation_date },
      { label: 'Garantie Tot', accessor: (i) => i.warranty_end },
      { label: 'Serienummer', accessor: (i) => i.serial_number },
    ],
    appointments: [
      { label: 'ID', accessor: (a) => a.id },
      { label: 'Titel', accessor: (a) => a.title },
      { label: 'Type', accessor: (a) => {
        const types = { cleaning: 'Reiniging', sales: 'Sales', maintenance: 'Onderhoud' };
        return types[a.appointment_type] || a.appointment_type;
      }},
      { label: 'Datum', accessor: (a) => a.date },
      { label: 'Tijd', accessor: (a) => a.time },
      { label: 'Duur (min)', accessor: (a) => a.duration_minutes },
      { label: 'Status', accessor: (a) => {
        const statuses = { scheduled: 'Gepland', completed: 'Voltooid', cancelled: 'Geannuleerd' };
        return statuses[a.status] || a.status;
      }},
      { label: 'Klant', accessor: (a) => {
        const cust = customers.find(c => c.id === a.customer_id);
        return cust ? `${cust.first_name} ${cust.last_name}` : '';
      }},
      { label: 'Adres', accessor: (a) => {
        const prop = properties.find(p => p.id === a.property_id);
        return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
      }},
      { label: 'Aangemaakt door', accessor: (a) => a.created_by_name },
      { label: 'Beschrijving', accessor: (a) => a.description },
    ],
    maintenance: [
      { label: 'ID', accessor: (m) => m.id },
      { label: 'Installatie ID', accessor: (m) => m.installation_id },
      { label: 'Datum', accessor: (m) => m.maintenance_date },
      { label: 'Type', accessor: (m) => m.maintenance_type },
      { label: 'Uitgevoerd door', accessor: (m) => m.performed_by },
      { label: 'Kosten', accessor: (m) => m.cost },
      { label: 'Notities', accessor: (m) => m.notes },
    ],
    cleanings: [
      { label: 'ID', accessor: (c) => c.id },
      { label: 'Property ID', accessor: (c) => c.property_id },
      { label: 'Adres', accessor: (c) => {
        const prop = properties.find(p => p.id === c.property_id);
        return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
      }},
      { label: 'Datum', accessor: (c) => c.cleaning_date },
      { label: 'Uitgevoerd door', accessor: (c) => c.performed_by },
      { label: 'Kosten', accessor: (c) => c.cost },
      { label: 'Notities', accessor: (c) => c.notes },
    ],
  };

  const handleExport = () => {
    if (activeTab === 'all') {
      // Export all datasets
      exportToCSV(customers, 'klanten', columnDefs.customers);
      setTimeout(() => exportToCSV(properties, 'adressen', columnDefs.properties), 100);
      setTimeout(() => exportToCSV(installations, 'installaties', columnDefs.installations), 200);
      setTimeout(() => exportToCSV(appointments, 'afspraken', columnDefs.appointments), 300);
      setTimeout(() => exportToCSV(maintenance, 'onderhoud', columnDefs.maintenance), 400);
      setTimeout(() => exportToCSV(cleanings, 'reinigingen', columnDefs.cleanings), 500);
    } else {
      exportToCSV(filteredData, activeTab, columnDefs[activeTab]);
    }
  };

  const resetFilters = () => {
    setFilters({
      hasSolarPanels: null,
      hasBattery: null,
      hasEVCharger: null,
      minPanels: 0,
      maxPanels: 100,
      cities: [],
      languages: [],
      hasCompany: null,
      installationTypes: [],
      dateFrom: '',
      dateTo: '',
      appointmentTypes: [],
      appointmentStatus: [],
    });
  };

  const toggleArrayFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.hasSolarPanels !== null) count++;
    if (filters.hasBattery !== null) count++;
    if (filters.hasEVCharger !== null) count++;
    if (filters.minPanels > 0 || filters.maxPanels < 100) count++;
    if (filters.languages.length > 0) count++;
    if (filters.cities.length > 0) count++;
    if (filters.hasCompany !== null) count++;
    if (filters.installationTypes.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.appointmentTypes.length > 0) count++;
    if (filters.appointmentStatus.length > 0) count++;
    return count;
  }, [filters]);

  const getDataCount = () => {
    if (activeTab === 'all') {
      return customers.length + properties.length + installations.length + appointments.length + maintenance.length + cleanings.length;
    }
    return Array.isArray(filteredData) ? filteredData.length : 0;
  };

  const tabConfig = [
    { value: 'customers', label: 'Klanten', icon: Users, count: customers.length },
    { value: 'properties', label: 'Adressen', icon: Building2, count: properties.length },
    { value: 'installations', label: 'Installaties', icon: Sun, count: installations.length },
    { value: 'appointments', label: 'Afspraken', icon: CalendarIcon, count: appointments.length },
    { value: 'maintenance', label: 'Onderhoud', icon: Wrench, count: maintenance.length },
    { value: 'cleanings', label: 'Reinigingen', icon: Sparkles, count: cleanings.length },
    { value: 'all', label: 'Alles', icon: FileSpreadsheet, count: null },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="export-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Data Export</h1>
          <p className="text-muted-foreground mt-1">
            Exporteer je CRM data naar CSV
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-filter-btn">
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-1 bg-primary text-primary-foreground">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between">
                  <span>Export Filters</span>
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </SheetTitle>
                <SheetDescription>
                  Filter data voordat je exporteert
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-2">
                {/* Installation Filters - for customers/properties/installations */}
                {['customers', 'installations'].includes(activeTab) && (
                  <FilterSection title="Installaties" icon={Sun} defaultOpen={true}>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Zonnepanelen</Label>
                        <div className="flex gap-2">
                          <Button 
                            variant={filters.hasSolarPanels === true ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === true ? null : true }))}
                            className="flex-1"
                          >
                            Heeft
                          </Button>
                          <Button 
                            variant={filters.hasSolarPanels === false ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: prev.hasSolarPanels === false ? null : false }))}
                            className="flex-1"
                          >
                            Geen
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Batterij</Label>
                        <div className="flex gap-2">
                          <Button 
                            variant={filters.hasBattery === true ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === true ? null : true }))}
                            className="flex-1"
                          >
                            Heeft
                          </Button>
                          <Button 
                            variant={filters.hasBattery === false ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasBattery: prev.hasBattery === false ? null : false }))}
                            className="flex-1"
                          >
                            Geen
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Laadpaal</Label>
                        <div className="flex gap-2">
                          <Button 
                            variant={filters.hasEVCharger === true ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === true ? null : true }))}
                            className="flex-1"
                          >
                            Heeft
                          </Button>
                          <Button 
                            variant={filters.hasEVCharger === false ? "default" : "outline"} 
                            size="sm"
                            onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: prev.hasEVCharger === false ? null : false }))}
                            className="flex-1"
                          >
                            Geen
                          </Button>
                        </div>
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Panel count filter */}
                {activeTab === 'customers' && (
                  <FilterSection title="Aantal Zonnepanelen" icon={Sun}>
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>{filters.minPanels} panelen</span>
                        <span>{filters.maxPanels}+ panelen</span>
                      </div>
                      <Slider
                        value={[filters.minPanels, filters.maxPanels]}
                        min={0}
                        max={100}
                        step={5}
                        onValueChange={([min, max]) => setFilters(prev => ({ ...prev, minPanels: min, maxPanels: max }))}
                      />
                    </div>
                  </FilterSection>
                )}

                {/* Installation Type filter */}
                {activeTab === 'installations' && (
                  <FilterSection title="Installatie Type" icon={Sun} defaultOpen={true}>
                    <div className="space-y-2 pt-2">
                      {[
                        { value: 'solar_panels', label: 'Zonnepanelen' },
                        { value: 'battery', label: 'Batterij' },
                        { value: 'ev_charger', label: 'Laadpaal' },
                        { value: 'inverter', label: 'Omvormer' }
                      ].map(type => (
                        <div key={type.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`inst-${type.value}`}
                            checked={filters.installationTypes.includes(type.value)}
                            onCheckedChange={() => toggleArrayFilter('installationTypes', type.value)}
                          />
                          <label htmlFor={`inst-${type.value}`} className="text-sm cursor-pointer">
                            {type.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FilterSection>
                )}

                {/* City filter */}
                {['customers', 'properties', 'installations'].includes(activeTab) && (
                  <FilterSection title="Gemeente" icon={MapPin}>
                    <div className="space-y-2 pt-2">
                      <Input
                        placeholder="Zoek gemeente..."
                        className="h-8 text-sm"
                        onChange={(e) => {
                          const items = e.target.parentElement.querySelectorAll('[data-city]');
                          items.forEach(item => {
                            const city = item.getAttribute('data-city').toLowerCase();
                            item.style.display = city.includes(e.target.value.toLowerCase()) ? '' : 'none';
                          });
                        }}
                      />
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {filterOptions.cities.slice(0, 50).map(city => (
                          <div key={city} data-city={city} className="flex items-center space-x-2 py-1">
                            <Checkbox
                              id={`city-exp-${city}`}
                              checked={filters.cities.includes(city)}
                              onCheckedChange={() => toggleArrayFilter('cities', city)}
                            />
                            <label htmlFor={`city-exp-${city}`} className="text-sm cursor-pointer truncate">
                              {city}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </FilterSection>
                )}

                {/* Language filter */}
                {activeTab === 'customers' && (
                  <FilterSection title="Taal" icon={Users}>
                    <div className="space-y-2 pt-2">
                      {['NL', 'FR', 'EN', 'DE'].map(lang => (
                        <div key={lang} className="flex items-center space-x-2">
                          <Checkbox
                            id={`lang-exp-${lang}`}
                            checked={filters.languages.includes(lang)}
                            onCheckedChange={() => toggleArrayFilter('languages', lang)}
                          />
                          <label htmlFor={`lang-exp-${lang}`} className="text-sm cursor-pointer">
                            {lang === 'NL' ? 'Nederlands' : lang === 'FR' ? 'Frans' : lang === 'EN' ? 'Engels' : 'Duits'}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FilterSection>
                )}

                {/* Company filter */}
                {activeTab === 'customers' && (
                  <FilterSection title="Klanttype" icon={Building2}>
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant={filters.hasCompany === true ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasCompany: prev.hasCompany === true ? null : true }))}
                        className="flex-1"
                      >
                        Zakelijk
                      </Button>
                      <Button 
                        variant={filters.hasCompany === false ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, hasCompany: prev.hasCompany === false ? null : false }))}
                        className="flex-1"
                      >
                        Particulier
                      </Button>
                    </div>
                  </FilterSection>
                )}

                {/* Appointment filters */}
                {activeTab === 'appointments' && (
                  <>
                    <FilterSection title="Afspraak Type" icon={CalendarIcon} defaultOpen={true}>
                      <div className="space-y-2 pt-2">
                        {[
                          { value: 'cleaning', label: 'Reiniging' },
                          { value: 'sales', label: 'Sales' },
                          { value: 'maintenance', label: 'Onderhoud' }
                        ].map(type => (
                          <div key={type.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`appt-${type.value}`}
                              checked={filters.appointmentTypes.includes(type.value)}
                              onCheckedChange={() => toggleArrayFilter('appointmentTypes', type.value)}
                            />
                            <label htmlFor={`appt-${type.value}`} className="text-sm cursor-pointer">
                              {type.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FilterSection>

                    <FilterSection title="Status" icon={Check}>
                      <div className="space-y-2 pt-2">
                        {[
                          { value: 'scheduled', label: 'Gepland' },
                          { value: 'completed', label: 'Voltooid' },
                          { value: 'cancelled', label: 'Geannuleerd' }
                        ].map(status => (
                          <div key={status.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`status-${status.value}`}
                              checked={filters.appointmentStatus.includes(status.value)}
                              onCheckedChange={() => toggleArrayFilter('appointmentStatus', status.value)}
                            />
                            <label htmlFor={`status-${status.value}`} className="text-sm cursor-pointer">
                              {status.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FilterSection>
                  </>
                )}

                {/* Date range filter */}
                {['appointments', 'maintenance', 'cleanings'].includes(activeTab) && (
                  <FilterSection title="Datum Bereik" icon={CalendarIcon}>
                    <div className="space-y-3 pt-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Van</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal h-9">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.dateFrom ? format(parse(filters.dateFrom, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={filters.dateFrom ? parse(filters.dateFrom, 'yyyy-MM-dd', new Date()) : undefined}
                              onSelect={(date) => date && setFilters(prev => ({ ...prev, dateFrom: format(date, 'yyyy-MM-dd') }))}
                              locale={nl}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tot</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal h-9">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.dateTo ? format(parse(filters.dateTo, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={filters.dateTo ? parse(filters.dateTo, 'yyyy-MM-dd', new Date()) : undefined}
                              onSelect={(date) => date && setFilters(prev => ({ ...prev, dateTo: format(date, 'yyyy-MM-dd') }))}
                              locale={nl}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </FilterSection>
                )}
              </div>

              <SheetFooter className="mt-6">
                <Button onClick={() => setFilterSheetOpen(false)} className="w-full">
                  Toepassen
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>

          <Button onClick={handleExport} disabled={loading} className="gap-2" data-testid="export-btn">
            <Download className="w-4 h-4" />
            Exporteer CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto gap-1 p-1">
          {tabConfig.map(tab => (
            <TabsTrigger 
              key={tab.value} 
              value={tab.value}
              className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.count !== null && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {tab.count}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="h-32 animate-pulse bg-muted" />
              ))}
            </div>
          ) : (
            <>
              {/* Active filters display */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-sm text-muted-foreground">Actieve filters:</span>
                  {filters.hasSolarPanels !== null && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasSolarPanels: null }))}>
                      {filters.hasSolarPanels ? 'Heeft zonnepanelen' : 'Geen zonnepanelen'}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  {filters.hasBattery !== null && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasBattery: null }))}>
                      {filters.hasBattery ? 'Heeft batterij' : 'Geen batterij'}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  {filters.hasEVCharger !== null && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, hasEVCharger: null }))}>
                      {filters.hasEVCharger ? 'Heeft laadpaal' : 'Geen laadpaal'}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  {filters.cities.length > 0 && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, cities: [] }))}>
                      {filters.cities.length} gemeente(n)
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  {(filters.dateFrom || filters.dateTo) && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }))}>
                      Datum filter
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs">
                    Wis alle
                  </Button>
                </div>
              )}

              {/* Data preview cards */}
              {activeTab === 'all' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {tabConfig.filter(t => t.value !== 'all').map(tab => (
                    <Card key={tab.value} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab(tab.value)}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                            <tab.icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{tab.count}</p>
                            <p className="text-sm text-muted-foreground">{tab.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>
                        {tabConfig.find(t => t.value === activeTab)?.label} Export
                      </span>
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        {Array.isArray(filteredData) ? filteredData.length : 0} rijen
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {activeFilterCount > 0 
                        ? `Gefilterde data klaar voor export`
                        : `Alle ${tabConfig.find(t => t.value === activeTab)?.label.toLowerCase()} worden geëxporteerd`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Preview table */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted">
                            <tr>
                              {columnDefs[activeTab]?.slice(0, 5).map((col, i) => (
                                <th key={i} className="px-4 py-3 text-left font-medium">{col.label}</th>
                              ))}
                              <th className="px-4 py-3 text-left font-medium">...</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {Array.isArray(filteredData) && filteredData.slice(0, 5).map((item, rowIndex) => (
                              <tr key={rowIndex} className="hover:bg-muted/50">
                                {columnDefs[activeTab]?.slice(0, 5).map((col, colIndex) => (
                                  <td key={colIndex} className="px-4 py-3 truncate max-w-[200px]">
                                    {col.accessor(item)}
                                  </td>
                                ))}
                                <td className="px-4 py-3 text-muted-foreground">...</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {Array.isArray(filteredData) && filteredData.length > 5 && (
                        <div className="px-4 py-3 bg-muted/50 text-sm text-muted-foreground text-center">
                          En nog {filteredData.length - 5} rijen meer...
                        </div>
                      )}
                    </div>

                    {/* Column info */}
                    <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm font-medium mb-2">Kolommen in export:</p>
                      <div className="flex flex-wrap gap-2">
                        {columnDefs[activeTab]?.map((col, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {col.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Export all summary */}
              {activeTab === 'all' && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileDown className="w-5 h-5" />
                      Volledige Database Export
                    </CardTitle>
                    <CardDescription>
                      Download alle data als 6 aparte CSV bestanden
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button onClick={handleExport} size="lg" className="w-full gap-2">
                      <Download className="w-5 h-5" />
                      Download Alle Data (6 CSV bestanden)
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Export;
